import React, { useEffect, useState } from "react";
import axios from "axios";
import { PacoteDTO } from "./types";
import styles from "../Styles/AssignedPackages.module.css";
import { useNavigate } from "react-router-dom";

interface AssignedPackagesTabProps {
    BASE_URL: string;
}

const AssignedPackagesTab: React.FC<AssignedPackagesTabProps> = ({ BASE_URL }) => {
    const [pacotes, setPacotes] = useState<PacoteDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("token");
        axios.get(`${BASE_URL}/motorista/pacotes-atribuidos`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => setPacotes(res.data))
            .catch(err => console.error("Erro ao carregar pacotes", err))
            .finally(() => setLoading(false));
    }, [BASE_URL]);

    if (loading) return <p>Loading packages...</p>;
    if (pacotes.length === 0) return <p>No assigned packages.</p>;

    const toggleExpand = (id: number) => setExpandedId(expandedId === id ? null : id);
    const goToDetails = (id: number) => navigate(`/flight-details/${id}`);

    return (
        <div className={styles.container}>
            {pacotes.map(p => {
                const isExpanded = expandedId === p.id;
                return (
                    <div key={p.id} className={`${styles.card} ${isExpanded ? styles.expanded : ''}`}>
                        {/* Header */}
                        <div className={styles.cardHeaderDropdown} onClick={() => toggleExpand(p.id)}>
                            <div className={styles.cardTitle}>
                                {p.fotoUrls[0] && (
                                    <img
                                        src={`${BASE_URL}${p.fotoUrls[0]}`}
                                        alt="Foto principal"
                                        className={styles.cardThumbnail}
                                    />
                                )}
                                <h5>{p.titulo}</h5>
                            </div>
                            <span className={styles.expandIcon}>{isExpanded ? "▲" : "▼"}</span>
                        </div>

                        {/* Conteúdo */}
                        {isExpanded && (
                            <div className={styles.cardBody}>
                                <p className={styles.description}>{p.descricao}</p>

                                {/* Info principal com estilo em “cards” */}
                                <div className={styles.infoGrid}>
                                    <div className={styles.infoCard}>
                                        <span className={styles.label}>Base Price</span>
                                        <span className={styles.value}>${p.precoBase}</span>
                                    </div>
                                    {p.servicoNome && (
                                        <div className={styles.infoCard}>
                                            <span className={styles.label}>Service</span>
                                            <span className={styles.value}>{p.servicoNome}</span>
                                        </div>
                                    )}
                                    {p.duracaoEmMinutos && (
                                        <div className={styles.infoCard}>
                                            <span className={styles.label}>Duration</span>
                                            <span className={styles.value}>{p.duracaoEmMinutos} min</span>
                                        </div>
                                    )}
                                    {p.idadeMinima && (
                                        <div className={styles.infoCard}>
                                            <span className={styles.label}>Minimum Age</span>
                                            <span className={styles.value}>{p.idadeMinima}</span>
                                        </div>
                                    )}
                                    {p.publicoAlvo && (
                                        <div className={styles.infoCard}>
                                            <span className={styles.label}>Target Audience</span>
                                            <span className={styles.value}>{p.publicoAlvo}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Pontos de interesse */}
                                {((p.pontosDeInteresse?.length || 0) > 0 || (p.pontosImportantes?.length || 0) > 0) && (
                                    <div className={styles.section}>
                                        {p.pontosDeInteresse?.length ? (
                                            <p><strong>Points of Interest:</strong> {p.pontosDeInteresse.join(', ')}</p>
                                        ) : null}
                                        {p.pontosImportantes?.length ? (
                                            <p><strong>Important Points:</strong> {p.pontosImportantes.join(', ')}</p>
                                        ) : null}
                                    </div>
                                )}

                                {/* Diferenciais */}
                                {(p.diferenciais?.length || 0) > 0 && (
                                    <div className={styles.section}>
                                        <strong>Differentials:</strong>
                                        <ul>
                                            {p.diferenciais?.map(d => (
                                                <li key={d.id}>
                                                    {d.tipo} {d.descricaoCustomizada ? `- ${d.descricaoCustomizada}` : ''} ({d.quantidade || '-'})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Fotos adicionais */}
                                {p.fotoUrls.length > 1 && (
                                    <div className={styles.imageGrid}>
                                        {p.fotoUrls.slice(1).map((url, i) => (
                                            <img key={i} src={`${BASE_URL}${url}`} alt={`Foto ${i + 1}`} className={styles.image} />
                                        ))}
                                    </div>
                                )}

                                {/* Botão ver detalhes */}
                                <button className={styles.detailsButton} onClick={() => goToDetails(p.id)}>
                                    See details
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AssignedPackagesTab;